import React, { useState, useEffect } from 'react';
import { App, Table, Tag, Input, Tabs } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import apiClient from '../services/api';
import { useDebouncedSearch } from '../hooks/useDebouncedSearch';
import { PAYMENT_STATUS_COLORS, getPaymentStatusLabel, FULFILLMENT_STATUS_COLORS, getFulfillmentStatusLabel } from '../utils/orderUtils';
import { formatCurrency, formatDate } from '../utils/format';

interface Order {
  id: string;
  display_id: number;
  email: string;
  total: number;
  currency_code: string;
  status: string;
  payment_status: string;
  fulfillment_status: string;
  created_at: string;
}

const Orders: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { message } = App.useApp();
  const navigate = useNavigate();
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(false);
  const { search, setSearch, debouncedSearch } = useDebouncedSearch();
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  useEffect(() => {
    loadOrders();
  }, [page, pageSize, statusFilter]);

  const loadOrders = async () => {
    try {
      setLoading(true);
      const params: any = {
        offset: (page - 1) * pageSize,
        limit: pageSize,
        fields: 'id,display_id,email,total,currency_code,status,payment_status,fulfillment_status,created_at',
      };
      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }
      const response = await apiClient.get('/admin/orders', { params });
      setOrders(response.data.orders || []);
      setTotal(response.data.count || 0);
    } catch (error: any) {
      message.error(error.response?.data?.message || t('orders.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const filteredData = debouncedSearch
    ? orders.filter(
        (o) =>
          String(o.display_id ?? '').toLowerCase().includes(debouncedSearch.toLowerCase()) ||
          o.email?.toLowerCase().includes(debouncedSearch.toLowerCase())
      )
    : orders;

  const columns = [
    {
      title: t('orders.displayId'),
      dataIndex: 'display_id',
      key: 'display_id',
      render: (text: number, record: Order) => (
        <a onClick={() => navigate(`/orders/${record.id}`)}>{text ?? record.id}</a>
      ),
    },
    {
      title: t('common.email'),
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: t('orders.total'),
      key: 'total',
      render: (_: any, record: Order) =>
        formatCurrency(record.total, record.currency_code, i18n.language),
    },
    {
      title: t('orders.status'),
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => {
        const colorMap: Record<string, string> = { completed: 'green', canceled: 'red' };
        const labelMap: Record<string, string> = {
          pending: t('orders.statusPending'),
          completed: t('orders.statusCompleted'),
          canceled: t('orders.statusCanceled'),
        };
        return (
          <Tag color={colorMap[status] || 'blue'}>
            {labelMap[status] || status}
          </Tag>
        );
      },
    },
    {
      title: t('orders.paymentStatus'),
      dataIndex: 'payment_status',
      key: 'payment_status',
      render: (status: string) => (
        <Tag color={PAYMENT_STATUS_COLORS[status] || 'default'}>
          {getPaymentStatusLabel(status, t)}
        </Tag>
      ),
    },
    {
      title: t('orders.fulfillmentStatus'),
      dataIndex: 'fulfillment_status',
      key: 'fulfillment_status',
      render: (status: string) => (
        <Tag color={FULFILLMENT_STATUS_COLORS[status] || 'blue'}>
          {getFulfillmentStatusLabel(status, t)}
        </Tag>
      ),
    },
    {
      title: t('common.createdAt'),
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date: string) => formatDate(date, i18n.language),
    },
  ];

  const tabItems = [
    { key: 'all', label: t('quotes.all') },
    { key: 'pending', label: t('orders.statusPending') },
    { key: 'completed', label: t('orders.statusCompleted') },
    { key: 'canceled', label: t('orders.statusCanceled') },
  ];

  return (
    <div style={{ padding: 24 }}>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h1 style={{ fontSize: 24, fontWeight: 600, color: '#334E3B', margin: 0 }}>
          {t('orders.title')}
        </h1>
        <Input
          placeholder={t('common.search')}
          prefix={<SearchOutlined />}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ width: 300 }}
          allowClear
        />
      </div>

      <Tabs
        activeKey={statusFilter}
        onChange={(key) => {
          setStatusFilter(key);
          setPage(1);
        }}
        items={tabItems}
        style={{ marginBottom: 16 }}
      />

      <Table
        columns={columns}
        dataSource={filteredData}
        loading={loading}
        rowKey="id"
        onRow={(record) => ({
          onClick: () => navigate(`/orders/${record.id}`),
          style: { cursor: 'pointer' },
        })}
        pagination={{
          current: page,
          pageSize: pageSize,
          total: total,
          showSizeChanger: true,
          onChange: (p, ps) => {
            setPage(p);
            setPageSize(ps);
          },
        }}
      />
    </div>
  );
};

export default Orders;
